import { OmitDeep } from 'type-fest';
import {
  ActivityLogsDocument,
  ActivityLogsQuery,
  ActivityLogsQueryVariables,
  api as generatedApi,
} from './generated';

export * from './generated';
export type ActivityLogChatEntry =
  ActivityLogsQuery['activityLogs']['nodes'][number];

generatedApi.reducerPath = 'backDashboardApi';

export const api = generatedApi
  .injectEndpoints({
    endpoints: (build) => ({
      ActivityLogsPaged: build.infiniteQuery<
        ActivityLogsQuery,
        OmitDeep<ActivityLogsQueryVariables, 'pagination.after'> | void,
        string | void
      >({
        infiniteQueryOptions: {
          initialPageParam: undefined,
          getNextPageParam: (lastPage) =>
            lastPage.activityLogs.pageInfo.hasNextPage
              ? lastPage.activityLogs.pageInfo.endCursor
              : undefined,
          getPreviousPageParam: (
            _firstPage,
            _allPages,
            _firstPageParam,
            allPageParams
          ) => allPageParams[allPageParams.length - 2] || undefined,
        },
        query: ({ queryArg, pageParam }) => ({
          document: ActivityLogsDocument,
          variables: {
            ...queryArg,
            pagination: { ...queryArg?.pagination, after: pageParam },
          },
        }),
      }),
    }),
  })
  .enhanceEndpoints({
    addTagTypes: [
      'Location',
      'Order',
      'Item',
      'ActivityLog',
      'User',
      'Invitation',
      'Inventory',
      'PrimaryLocation',
      'SecondaryLocation',
      'Config',
      'Roles',
    ],
    endpoints: {
      GetAppConfig: {
        providesTags: ['Config'],
      },
      GetCurrentRoles: {
        providesTags: ['Roles'],
      },
      ActivityLogsCount: {
        providesTags(result, error) {
          if (error) return [];

          return [{ type: 'ActivityLog' as const, id: 'COUNT' }];
        },
      },
      ActivityLogs: {
        providesTags(result, error) {
          if (error) return [];

          const entries = result?.activityLogs.nodes || [];

          return [
            { type: 'ActivityLog' as const, id: 'LIST' },
            ...entries.map((entry) => ({
              type: 'ActivityLog' as const,
              id: entry._id,
            })),
          ];
        },
      },
      Invitation: {
        providesTags(result, error, arg) {
          if (error) return [];

          return [{ type: 'Invitation' as const, id: arg.invitationId }];
        },
      },
      Invitations: {
        providesTags(result, error) {
          if (error) return [];

          const entries = result?.invitations.nodes || [];

          return [
            { type: 'Invitation' as const, id: 'LIST' },
            ...entries.map((entry) => ({
              type: 'Invitation' as const,
              id: entry._id,
            })),
          ];
        },
      },
      CreateInvitation: {
        invalidatesTags: (result, error) => {
          if (error || !result?.createInvitation.result) {
            return [];
          }

          return [{ type: 'Invitation' as const, id: 'LIST' }];
        },
      },
      UpdateInvitationOwnership: {
        invalidatesTags: (result, error, request) => {
          if (error || !result?.updateInvitationOwnership.result) {
            return [];
          }

          return [
            { type: 'Invitation' as const, id: request.invitationId },
            { type: 'Invitation' as const, id: 'LIST' },
            { type: 'Order' as const, id: 'LIST' },
          ];
        },
      },
      Item: {
        providesTags(result, error, arg) {
          if (error) return [];

          return [{ type: 'Item' as const, id: arg.itemId }];
        },
      },
      Items: {
        providesTags(result, error) {
          if (error) return [];

          const entries = result?.items.nodes || [];

          return [
            { type: 'Item' as const, id: 'LIST' },
            ...entries.map((entry) => ({
              type: 'Item' as const,
              id: entry._id,
            })),
          ];
        },
      },
      UpdateItemStatus: {
        invalidatesTags: (result, error, request) => {
          if (error) return [];

          const input = Array.isArray(request.bulkUpdateItemStatusInput)
            ? request.bulkUpdateItemStatusInput
            : [request.bulkUpdateItemStatusInput];

          return [
            { type: 'Order' as const, id: 'LIST' },
            ...input.map(({ orderId }) => ({
              type: 'Order' as const,
              id: orderId,
            })),
            { type: 'Inventory' as const, id: 'LIST' },
            { type: 'User' as const, id: 'LIST' },
            { type: 'ActivityLog' as const, id: 'LIST' },
            { type: 'ActivityLog' as const, id: 'COUNT' },
          ];
        },
      },
      BulkCreateLocalItems: {
        invalidatesTags: (result, error) => {
          if (error) return [];

          return [
            { type: 'Item' as const, id: 'LIST' },
            { type: 'ActivityLog' as const, id: 'LIST' },
            { type: 'ActivityLog' as const, id: 'COUNT' },
          ];
        },
      },
      BulkUpdateLocalItems: {
        invalidatesTags: (result, error, request) => {
          if (error) {
            return [];
          }

          const products = Array.isArray(request.product)
            ? request.product
            : [request.product];

          return [
            { type: 'Item' as const, id: 'LIST' },
            ...products.map((item) => ({
              type: 'Item' as const,
              id: item._id,
            })),
            { type: 'ActivityLog' as const, id: 'LIST' },
            { type: 'ActivityLog' as const, id: 'COUNT' },
          ];
        },
      },
      BulkRecallItems: {
        invalidatesTags: (result, error, request) => {
          if (error || !result?.bulkRecallItems.result) {
            return [];
          }

          return [
            { type: 'Order' as const, id: 'LIST' },
            { type: 'User' as const, id: 'LIST' },
            { type: 'Inventory' as const, id: 'LIST' },
            { type: 'ActivityLog' as const, id: 'LIST' },
            { type: 'ActivityLog' as const, id: 'COUNT' },
          ];
        },
      },
      RecallUserItems: {
        invalidatesTags: (result, error, request) => {
          if (error || !result?.recallUserItems.result) {
            return [];
          }

          return [
            { type: 'Order' as const, id: 'LIST' },
            { type: 'User' as const, id: 'LIST' },
            { type: 'Inventory' as const, id: 'LIST' },
            { type: 'ActivityLog' as const, id: 'LIST' },
            { type: 'ActivityLog' as const, id: 'COUNT' },
          ];
        },
      },
      BulkDeleteLocalItems: {
        invalidatesTags: (result, error, request) => {
          if (error) return [];

          const itemIds = Array.isArray(request.itemIds)
            ? request.itemIds
            : [request.itemIds];

          return [
            { type: 'Item' as const, id: 'LIST' },
            ...itemIds.map((itemId) => ({
              type: 'Item' as const,
              id: itemId,
            })),
            { type: 'ActivityLog' as const, id: 'LIST' },
            { type: 'ActivityLog' as const, id: 'COUNT' },
          ];
        },
      },
      Locations: {
        providesTags(result, error) {
          if (error) return [];

          const entries = result?.locations.nodes || [];

          return [
            { type: 'Location' as const, id: 'LIST' },
            ...entries.map((entry) => ({
              type: 'Location' as const,
              id: entry._id,
            })),
          ];
        },
      },
      SuperadminLocations: {
        providesTags(result, error) {
          if (error) return [];

          const entries = result?.superadminLocations.nodes || [];

          return [
            { type: 'Location' as const, id: 'LIST' },
            ...entries.map((entry) => ({
              type: 'Location' as const,
              id: entry._id,
            })),
          ];
        },
      },
      Location: {
        providesTags(result, error, arg) {
          if (error) return [];

          return [{ type: 'Location' as const, id: arg.locationId }];
        },
      },
      PrimaryLocations: {
        providesTags(result, error) {
          if (error) return [];

          return [
            ...(result?.atkomstClients || []).map((location) => ({
              type: 'PrimaryLocation' as const,
              id: location.id,
            })),
          ];
        },
      },
      SecondaryLocations: {
        providesTags(result, error) {
          if (error) return [];

          return [
            ...(result?.cdsLocations || []).map((location) => ({
              type: 'SecondaryLocation' as const,
              id: location.id,
            })),
          ];
        },
      },
      UpdateLocation: {
        invalidatesTags: (result, error, request) => {
          if (error) return [];

          return [
            { type: 'Location' as const, id: request.locationId },
            { type: 'ActivityLog' as const, id: 'LIST' },
            { type: 'ActivityLog' as const, id: 'COUNT' },
          ];
        },
      },

      CreateLocation: {
        invalidatesTags: (result, error, request) => {
          if (error) return [];

          return [
            // Invalidate location list to refetch
            { type: 'Location' as const, id: 'LIST' },
          ];
        },
      },

      CreateWarehouse: {
        invalidatesTags: (result, error, request) => {
          if (error) return [];

          return [
            { type: 'Location' as const, id: request.locationId },
            { type: 'ActivityLog' as const, id: 'LIST' },
            { type: 'ActivityLog' as const, id: 'COUNT' },
          ];
        },
      },
      UpdateWarehouse: {
        invalidatesTags: (result, error, request) => {
          if (error) return [];

          return [
            { type: 'Location' as const, id: request.locationId },
            { type: 'ActivityLog' as const, id: 'LIST' },
            { type: 'ActivityLog' as const, id: 'COUNT' },
          ];
        },
      },
      DeleteWarehouse: {
        invalidatesTags: (result, error, request) => {
          if (error) return [];

          return [
            { type: 'Location' as const, id: request.locationId },
            { type: 'ActivityLog' as const, id: 'LIST' },
            { type: 'ActivityLog' as const, id: 'COUNT' },
          ];
        },
      },
      BulkEnableStockItem: {
        invalidatesTags: (result, error, request) => {
          if (error) return [];

          return [
            { type: 'Location' as const, id: request.locationId },
            { type: 'ActivityLog' as const, id: 'LIST' },
            { type: 'ActivityLog' as const, id: 'COUNT' },
          ];
        },
      },
      BulkDisableStockItem: {
        invalidatesTags: (result, error, request) => {
          if (error) return [];

          return [
            { type: 'Location' as const, id: request.locationId },
            { type: 'ActivityLog' as const, id: 'LIST' },
            { type: 'ActivityLog' as const, id: 'COUNT' },
          ];
        },
      },
      UpdateSettings: {
        invalidatesTags: (result, error, request) => {
          if (error) return [];

          return [
            { type: 'Location' as const, id: request.locationId },
            { type: 'ActivityLog' as const, id: 'LIST' },
            { type: 'ActivityLog' as const, id: 'COUNT' },
          ];
        },
      },
      BulkUpdateStock: {
        invalidatesTags: (result, error, request) => {
          if (error) return [];

          return [
            { type: 'Location' as const, id: request.input.locationId },
            { type: 'ActivityLog' as const, id: 'LIST' },
            { type: 'ActivityLog' as const, id: 'COUNT' },
          ];
        },
      },
      BulkRemoveStockItem: {
        invalidatesTags: (result, error, request) => {
          if (error) return [];

          return [
            { type: 'Location' as const, id: request.locationId },
            { type: 'ActivityLog' as const, id: 'LIST' },
            { type: 'ActivityLog' as const, id: 'COUNT' },
          ];
        },
      },
      Orders: {
        providesTags(result, error) {
          if (error) return [];

          const entries = result?.orders.nodes || [];

          return [
            { type: 'Order' as const, id: 'LIST' },
            ...entries.map((entry) => ({
              type: 'Order' as const,
              id: entry._id,
            })),
          ];
        },
      },
      Order: {
        providesTags(result, error, arg) {
          if (error) return [];

          return [{ type: 'Order' as const, id: arg.orderId }];
        },
      },
      createOrder: {
        invalidatesTags: (result, error, request) => {
          if (error) return [];

          const commonTags = [
            { type: 'Order' as const, id: 'LIST' },
            { type: 'ActivityLog' as const, id: 'LIST' },
            { type: 'ActivityLog' as const, id: 'COUNT' },
            { type: 'Location' as const, id: request.locationId },
          ];

          return request.createOrderInput.invitationId
            ? [
                ...commonTags,
                {
                  type: 'Invitation' as const,
                  id: request.createOrderInput.invitationId,
                },
              ]
            : [...commonTags];
        },
      },
      CommentOrder: {
        invalidatesTags: (result, error, request) => {
          if (error) return [];

          return [
            { type: 'Order' as const, id: request.commentOrderInput.orderId },
            { type: 'ActivityLog' as const, id: 'LIST' },
            { type: 'ActivityLog' as const, id: 'COUNT' },
          ];
        },
      },
      BulkUpdateOrderOnHold: {
        invalidatesTags: (result, error, request) => {
          if (error) return [];

          const orderIds = Array.isArray(request.orderIds)
            ? request.orderIds
            : [request.orderIds];

          return [
            { type: 'Order' as const, id: 'LIST' },
            ...orderIds.map((orderId) => ({
              type: 'Order' as const,
              id: orderId,
            })),
            { type: 'ActivityLog' as const, id: 'LIST' },
            { type: 'ActivityLog' as const, id: 'COUNT' },
          ];
        },
      },
      User: {
        providesTags(result, error, arg) {
          if (error) return [];

          return [{ type: 'User' as const, id: arg.email }];
        },
      },
      Users: {
        providesTags(result, error) {
          if (error) return [];

          const entries = result?.users.nodes || [];

          return [
            { type: 'User' as const, id: 'LIST' },
            ...entries.map((entry) => ({
              type: 'User' as const,
              id: entry.email,
            })),
          ];
        },
      },
      Inventory: {
        providesTags(result, error) {
          if (error) return [];

          return [{ type: 'Inventory' as const, id: 'LIST' }];
        },
      },
      AddExternalCoworker: {
        invalidatesTags: (result, error, request) => {
          if (error) return [];

          return [
            { type: 'User' as const, id: 'LIST' },
            { type: 'User' as const, id: request.email },
          ];
        },
      },
      RemoveExternalCoworker: {
        invalidatesTags: (result, error, request) => {
          if (error) return [];

          return [
            { type: 'User' as const, id: 'LIST' },
            { type: 'User' as const, id: request.email },
          ];
        },
      },
      UpdateUserRoles: {
        invalidatesTags: (result, error, request) => {
          if (error) return [];

          return [
            { type: 'User' as const, id: 'LIST' },
            { type: 'User' as const, id: request.email },
          ];
        },
      },
    },
  });

export const { useActivityLogsPagedInfiniteQuery } = api;
